import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { ThemeProvider } from 'styled-components';
import {
  withPrismicUnpublishedPreview,
  componentResolverFromMap,
} from 'gatsby-plugin-prismic-previews';

import { linkResolver } from '../utils/linkResolver';
import Layout from '../components/Layout';
import LayoutPartnerPage from '../components/LayoutPartnerPage';
import PageBody from '../components/PageBody';
import SEO from '../components/SEO';
import theme from '../ui/theme';
import ContainerHeader from '../components/ContainerHeader';

import NewsTemplate from './newsTemplate';
import EventTemplate from './eventTemplate';

const PageTemplate = ({ data: { prismicPage }, location, pageContext }) => {
  const isPartnerPage = Boolean(prismicPage.data.partner_company);
  const hasHero =
    prismicPage.data.body.findIndex((part) => part.slice_type === 'hero') !==
    -1;

  const LayoutComponent = isPartnerPage ? LayoutPartnerPage : Layout;

  const customTheme = {
    ...theme,
    colors: {
      ...theme.colors,
      ...(prismicPage.data.partner_color_1
        ? { partnerColor1: prismicPage.data.partner_color_1 }
        : {}),
      ...(prismicPage.data.partner_color_2
        ? { partnerColor2: prismicPage.data.partner_color_2 }
        : {}),
      ...(prismicPage.data.partner_color_3
        ? { partnerColor3: prismicPage.data.partner_color_3 }
        : {}),
    },
  };

  return (
    <ThemeProvider theme={customTheme}>
      <LayoutComponent
        selectedLocale={pageContext.lang}
        location={location}
        partnerLogo={prismicPage.data.partner_logo}
        hasHero={hasHero}
      >
        <SEO
          selectedLocale={pageContext.lang}
          location={location}
          specifics={{
            title: prismicPage.data.meta_title,
            description: prismicPage.data.meta_description,
            image: prismicPage.data.share_image.url,
          }}
        />
        {!hasHero ? (
          <ContainerHeader
            py={{
              mobile: isPartnerPage ? 'baseUp4' : 'baseUp6',
              smallTablet: isPartnerPage ? 'baseUp6' : 'baseUp8',
              tablet: isPartnerPage ? 'baseUp6' : 'baseUp10',
            }}
            backgroundColor="grayLight"
          />
        ) : null}
        <PageBody
          content={prismicPage.data.body}
          selectedLocale={pageContext.lang}
        />
      </LayoutComponent>
    </ThemeProvider>
  );
};

PageTemplate.propTypes = {
  data: PropTypes.shape({
    prismicPage: PropTypes.shape({
      data: PropTypes.shape({
        meta_title: PropTypes.string,
        meta_description: PropTypes.string,
        share_image: PropTypes.shape({
          url: PropTypes.string,
        }),
        partner_company: PropTypes.string,
        partner_logo: PropTypes.shape({
          alt: PropTypes.string,
          url: PropTypes.string,
          localFile: PropTypes.shape({
            childImageSharp: PropTypes.shape({}),
          }),
        }),
        partner_color_1: PropTypes.string,
        partner_color_2: PropTypes.string,
        partner_color_3: PropTypes.string,
        body: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    }),
  }).isRequired,
  location: PropTypes.shape({}).isRequired,
  pageContext: PropTypes.shape({ lang: PropTypes.string }).isRequired,
};

export default withPrismicUnpublishedPreview(PageTemplate, [
  {
    repositoryName: process.env.GATSBY_PRISMIC_REPO_NAME,
    linkResolver,
    componentResolver: componentResolverFromMap({
      page: PageTemplate,
      news: NewsTemplate,
      event: EventTemplate,
    }),
  },
]);

export const query = graphql`
  query ($id: String!) {
    prismicPage(id: { eq: $id }) {
      _previewable
      data {
        meta_title
        meta_description
        share_image {
          url
        }
        partner_company
        partner_logo {
          alt
          url
        }
        partner_color_1
        partner_color_2
        partner_color_3
        body {
          ... on PrismicPageDataBodyHero {
            ...PageHeroFragment
          }
          ... on PrismicPageDataBodyHeroCarousel {
            ...PageHeroCarouselFragment
          }
          ... on PrismicPageDataBodyLogoRibbon {
            id
            slice_type
          }
          ... on PrismicPageDataBodyLogosRow {
            ...LogosRowFragment
          }
          ... on PrismicPageDataBodySteps {
            ...PageStepsFragment
          }
          ... on PrismicPageDataBodyCallOut {
            ...PageCallOutFragment
          }
          ... on PrismicPageDataBodyCaseNews {
            ...PageCaseAndNewsFragment
          }
          ... on PrismicPageDataBodyStatistics {
            ...PageStatisticsFragment
          }
          ... on PrismicPageDataBodyText {
            ...PageTextFragment
          }
          ... on PrismicPageDataBodyTextMedia {
            ...PageTextMediaFragment
          }
          ... on PrismicPageDataBodyEvents {
            ...PageEventsFragment
          }
          ... on PrismicPageDataBodyComparisonTable {
            ...PageComparisonTableFragment
          }
          ... on PrismicPageDataBodyNewsList {
            ...PageNewsListFragment
          }
          ... on PrismicPageDataBodySubscibeNewsletter {
            ...PageSubscribeNewsletterFragment
          }
          ... on PrismicPageDataBodyFaqs {
            ...PageFAQsFragment
          }
          ... on PrismicPageDataBodyForm {
            ...PageFormFragment
          }
          ... on PrismicPageDataBodyTeaserCards {
            ...PageTeaserCardsFragment
          }
          ... on PrismicPageDataBodyUspCarousel {
            ...PageUSPCarouselFragment
          }
          ... on PrismicPageDataBodyTabs {
            ...PageTabsFragment
          }
          ... on PrismicPageDataBodyFullVideo {
            ...PageFullVideoFragment
          }
          ... on PrismicPageDataBodyCollaborationPartners {
            ...PageCollaborationPartnersFragment
          }
          ... on PrismicPageDataBodyImageGrid {
            ...PageImageGridFragment
          }
        }
      }
    }
  }
`;
